import { isAntonioError, type AntonioError } from '@ackee/antonio-core';

import type { ApiError, SaasLimits } from '@/types';

export enum ErrorCode {
    IMPORT_VALIDATION_INVALID_FILE = 'e1002',
    IMPORT_VALIDATION_INVALID_SECRETS = 'e1003',
    MISSING_CLIENT_SECRET = 'e1007',
    FIRST_LOGIN_BY_ADMIN = 'e1010',
    ACCOUNT_NOT_IN_WORKSPACE = 'e1017',
    CONFIGURED_SUPER_ADMIN_IS_NOT_AN_ADMIN = 'e1023',
    CONFIGURED_SUPER_ADMIN_USER_DOESNT_EXIST_IN_THE_WORKSPACE = 'e1024',
    AUTH_CLIENT_IS_NOT_CONFIGURED = 'e1025',
    SAAS_LIMIT = 'e2000',
    SHARING_LINK_EXPIRED = 'e1037',
    USER_NOT_A_MEMBER_OF_A_WORKSPACE = 'e1038',
    EXECUTABLE_FILE_NOT_ALLOWED = 'e1039',
    LAST_ADMIN_REMOVAL = 'e1043',
    USER_ACCESS_NOT_ALLOWED = 'e2005',
    VAULTS_NOT_AVAILABLE = 'e2006',
    TEAM_VAULT_LIMIT_REACHED = 'e2007',
}

export const isErrorCode = (value: string | null): value is ErrorCode => Object.values(ErrorCode).includes(value as ErrorCode);

export type ApiAntonioError<T extends Record<string, any>> = AntonioError<ApiError<T, `${ErrorCode}`>>;

export function isApiAntonioError<T extends Record<string, any>>(e: any): e is ApiAntonioError<T> {
    if (isAntonioError(e)) {
        const error = e as AntonioError<any>;
        return error.data?.errorCode;
    }
    return false;
}

export const isSaasLimitReachedError = (e: any): e is ApiAntonioError<SaasLimits> =>
    isApiAntonioError(e) && e.data.errorCode === ErrorCode.SAAS_LIMIT;

export const isExecutableFileError = (e: unknown): boolean =>
    isApiAntonioError(e) && e.data.errorCode === ErrorCode.EXECUTABLE_FILE_NOT_ALLOWED;

export const isLastAdminRemovalError = (e: unknown): boolean => isApiAntonioError(e) && e.data.errorCode === ErrorCode.LAST_ADMIN_REMOVAL;

/**
 * The vault gating responses carry their code in the nested `errorData` rather than the top-level
 * `errorCode` the older errors use, so `isApiAntonioError` — which gates on the top-level field being
 * truthy — never matches them. Read both, nested first.
 */
const getApiErrorCode = (e: unknown): string | undefined => {
    if (!isAntonioError(e)) {
        return undefined;
    }

    const { data } = e as AntonioError<Partial<ApiError<{ code?: string }>>>;

    return data?.errorData?.code ?? data?.errorCode;
};

export const isVaultsNotAvailableError = (e: unknown) => getApiErrorCode(e) === ErrorCode.VAULTS_NOT_AVAILABLE;

export const isTeamVaultLimitReachedError = (e: unknown) => getApiErrorCode(e) === ErrorCode.TEAM_VAULT_LIMIT_REACHED;

/**
 * Status rather than code, because a bad request carries no code in the shared error schema. The
 * transport reports it on the response; the error body repeats it, which is the shape the vault
 * responses are specified with.
 */
export const isBadRequestError = (e: unknown): boolean => {
    if (!isAntonioError(e)) {
        return false;
    }

    const { response, data } = e as { response?: { status?: number }; data?: { status?: number } };

    return (response?.status ?? data?.status) === 400;
};

export const hasExistingEnterpriseInstallation = (e: unknown): e is ApiAntonioError<{ appUrl: string }> =>
    isApiAntonioError(e) &&
    [ErrorCode.FIRST_LOGIN_BY_ADMIN, ErrorCode.AUTH_CLIENT_IS_NOT_CONFIGURED].includes(e.data.errorCode) &&
    !!e.data.errorData?.appUrl;

export const isExpiredShareLinkError = (e: unknown): e is ApiAntonioError<{ secretId: string }> => {
    return (
        isApiAntonioError<{ secretId: string }>(e) &&
        e.data.errorCode === ErrorCode.SHARING_LINK_EXPIRED &&
        typeof e.data.errorData?.secretId === 'string'
    );
};
